import React, { useState, useRef, useEffect } from 'react';
import CONSTANTS, { ContentTypeConstants } from '../../constants/contentCreationConstants';

const QuestionSelector = ({
  contentType,
  selectedDifficulty,
  currentFile,
  DisplayFiles,
  handleRadioChangeDiff,
  handleQuestionChange,
  selectedVideo,
  setSelectedVideoWithSession,
  selectedNote,
  setSelectedNoteWithSession,
  selectedQuestion,
  setSelectedQuestionWithSession,
  allVideos = {},
  allNotes = {},
  videoDetails = null,
  videoTitle = "",
  notesTitle = "",
  freezeFilters = false
}) => {
  // Ensure freezeFilters is a boolean (handle string "false" or other truthy/falsy values)
  const isFrozen = freezeFilters === true || freezeFilters === 'true';
  
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  // Debug: Track isOpen state changes
  useEffect(() => {
  }, [isOpen]);

  // Debug logging for props changes
  useEffect(() => {
    // Props change tracking removed - no action needed
  }, [contentType, currentFile, selectedVideo, selectedNote, selectedQuestion, videoTitle, videoDetails, DisplayFiles, freezeFilters, isFrozen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      } else {
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Get filtered options
  const getFilteredOptions = () => {
    if (!DisplayFiles) return [];

    if (contentType === ContentTypeConstants.NOTES.key) {
      // Handle notes_by_level structure
      if (DisplayFiles.notes_by_level) {
        // Check if no level is selected (could be "default", "Select", or undefined)
        if (selectedDifficulty === CONSTANTS.DEFAULT || selectedDifficulty === "default" || selectedDifficulty === "Select" || !selectedDifficulty) {
          // Return all notes from all levels
          return Object.values(DisplayFiles.notes_by_level).flat();
        } else {
          // Return notes for specific level
          return DisplayFiles.notes_by_level[selectedDifficulty] || [];
        }
      }
      // Fallback for array structure
      return Array.isArray(DisplayFiles) ? DisplayFiles.filter(note => {
        if (selectedDifficulty === CONSTANTS.DEFAULT) return true;
        return note.level && note.level === selectedDifficulty;
      }) : [];
    } else if (contentType === ContentTypeConstants.VIDEO.key) {
      // Handle videos_by_level structure
      if (DisplayFiles.videos_by_level) {
        // Check if no level is selected (could be "default", "Select", or undefined)
        if (selectedDifficulty === CONSTANTS.DEFAULT || selectedDifficulty === "default" || selectedDifficulty === "Select" || !selectedDifficulty) {
          // Return all videos from all levels
          return Object.values(DisplayFiles.videos_by_level).flat();
        } else {
          // Return videos for specific level
          return DisplayFiles.videos_by_level[selectedDifficulty] || [];
        }
      }
      // Fallback for array structure
      return Array.isArray(DisplayFiles) ? DisplayFiles.filter(video => {
        if (selectedDifficulty === CONSTANTS.DEFAULT) return true;
        return video.level && video.level === selectedDifficulty;
      }) : [];
    } else if (contentType === ContentTypeConstants.MCQ.key || contentType === ContentTypeConstants.QUIZ.key || contentType === ContentTypeConstants.CODING.key || contentType === ContentTypeConstants.PROJECT_CODING.key) {
      // Handle questions (array of question IDs)
      return Array.isArray(DisplayFiles) ? DisplayFiles : [];
    } else {
      // Handle questions (array structure)
      return Array.isArray(DisplayFiles) ? DisplayFiles.filter(fileName => {
        if (typeof fileName !== 'string') return false;
        const cleanFileName = fileName.endsWith('.json') ? fileName.slice(0, -5) : fileName;
        const difficultyChar = cleanFileName.charAt(cleanFileName.length - 4).toUpperCase();
        
        if (selectedDifficulty === CONSTANTS.DEFAULT) return true;
        if (selectedDifficulty === "level1" && difficultyChar === "E") return true;
        if (selectedDifficulty === "level2" && difficultyChar === "M") return true;
        if (selectedDifficulty === "level3" && difficultyChar === "H") return true;
        return false;
      }) : [];
    }
  };

  let filteredOptions = getFilteredOptions();
  
  // Define content type checks first (reused in multiple places)
  const isVideoType = contentType === ContentTypeConstants.VIDEO.key;
  const isNotesType = contentType === ContentTypeConstants.NOTES.key;
  const isQuestionType = contentType === ContentTypeConstants.MCQ.key || contentType === ContentTypeConstants.QUIZ.key || contentType === ContentTypeConstants.CODING.key || contentType === ContentTypeConstants.PROJECT_CODING.key;
  
  // Ensure selected video/note/question is included in options even if not in filteredOptions
  // This handles cases where the selected item might be in a different level
  const effectiveId = currentFile !== CONSTANTS.DEFAULT_QN 
    ? currentFile 
    : (isNotesType && selectedNote) 
      ? selectedNote 
      : (isVideoType && selectedVideo) 
        ? selectedVideo 
        : (isQuestionType && selectedQuestion)
          ? selectedQuestion
          : null;
  
  // Only ensure a *real* selected entity is in the list. defaultQn is a sentinel for "Create New", not an option.
  const isRealEntityId = effectiveId && effectiveId !== CONSTANTS.DEFAULT_QN;

  if (isRealEntityId && (isVideoType || isNotesType || isQuestionType)) {
    // Check if already in options - for questions, options are strings, for videos/notes they're objects with id
    const isAlreadyInOptions = isQuestionType
      ? filteredOptions.some(option => String(option) === String(effectiveId))
      : filteredOptions.some(option => String(option.id) === String(effectiveId));
    
    if (!isAlreadyInOptions) {
      // Try to find in allVideos/allNotes
      if (isVideoType) {
        if (allVideos && Object.keys(allVideos).length > 0) {
          const allVideosList = Object.values(allVideos).flat();
          const videoOption = allVideosList.find(video => String(video.id) === String(effectiveId));
          if (videoOption) {
            filteredOptions = [videoOption, ...filteredOptions];
          }
        }
        
        // If still not found, create synthetic option so the selected video appears (e.g. when from another level)
        if (!filteredOptions.some(option => String(option.id) === String(effectiveId))) {
          if (videoDetails || videoTitle || String(selectedVideo) === String(effectiveId)) {
            const title = videoTitle || (videoDetails && videoDetails.title) || `Video ${effectiveId}`;
            const level = (videoDetails && videoDetails.level) || selectedDifficulty;
            filteredOptions = [{
              id: effectiveId,
              title: title,
              video: title,
              level: level
            }, ...filteredOptions];
          }
        }
      } else if (isNotesType) {
        if (allNotes && Object.keys(allNotes).length > 0) {
          const allNotesList = Object.values(allNotes).flat();
          const noteOption = allNotesList.find(note => String(note.id) === String(effectiveId));
          if (noteOption) {
            filteredOptions = [noteOption, ...filteredOptions];
          }
        }
        
      } else if (isQuestionType) {
        // For MCQ/Coding questions, check if the question exists in DisplayFiles
        const questionExists = DisplayFiles && DisplayFiles.length > 0 && DisplayFiles.some(question => String(question) === String(effectiveId));
        const alreadyInOptions = filteredOptions.some(opt => String(opt) === String(effectiveId));
        
        if (!questionExists && !alreadyInOptions) {
          // If question doesn't exist in DisplayFiles but we have effectiveId, add it to options
          // This ensures the selected question is visible in the dropdown
          filteredOptions = [effectiveId, ...filteredOptions];
        } else {
        }
      }
    }
  }

  const handleOptionClick = (value) => {
    // Prevent option selection when filters are frozen
    if (isFrozen) {
      setIsOpen(false);
      return;
    }
    
    if (contentType === ContentTypeConstants.VIDEO.key) {
      // For videos, use the video ID and save to session storage
      setSelectedVideoWithSession(value);
      handleQuestionChange({ target: { value } });
    } else if (contentType === ContentTypeConstants.NOTES.key) {
      // For notes, use the note ID and save to session storage
      setSelectedNoteWithSession(value);
      handleQuestionChange({ target: { value } });
    } else if (contentType === ContentTypeConstants.MCQ.key || contentType === ContentTypeConstants.QUIZ.key || contentType === ContentTypeConstants.CODING.key || contentType === ContentTypeConstants.PROJECT_CODING.key) {
      // For MCQs, Coding, and Project Coding, use the question ID and save to session storage
      setSelectedQuestionWithSession(value);
      handleQuestionChange({ target: { value } });
    } else {
    handleQuestionChange({ target: { value } });
    }
    setIsOpen(false);
  };

  const getDisplayText = (option) => {
    if (contentType === ContentTypeConstants.NOTES.key) {
      // Always prioritize notes property (contains "notes1", "notes2", etc.) over title
      return option.notes || option.title || option.name || `Notes ${option.id || 'Unknown'}`;
    } else if (contentType === ContentTypeConstants.VIDEO.key) {
      // Always prioritize video property (contains "video1", "video2", etc.) over title
      // This ensures we show numbers instead of titles when coming from ContentBank
      return option.video || option.title || option.name || option.description || `Video ${option.id || 'Unknown'}`;
    } else if (contentType === ContentTypeConstants.MCQ.key || contentType === ContentTypeConstants.QUIZ.key) {
      // For MCQs, format question ID as last 3 characters with first character capitalized
      if (typeof option === 'string') {
        const last2Chars = option.slice(-2);
        const last4Char = option.slice(-4,-3).toUpperCase();
        return `${last4Char}${last2Chars}`;
      }
      return `${option.id || 'Unknown'}`;
    } else if (contentType === ContentTypeConstants.CODING.key || contentType === ContentTypeConstants.PROJECT_CODING.key) {
      // For Coding and Project Coding, format question ID as last 3 characters with first character capitalized
      if (typeof option === 'string') {
        const last2Chars = option.slice(-2);
        const last4Char = option.slice(-4,-3).toUpperCase();
        return `${last4Char}${last2Chars}`;
      }
      return `${option.id || 'Unknown'}`;
    } else {
      const cleanFileName = option.endsWith('.json') ? option.slice(0, -5) : option;
      const difficultyChar = cleanFileName.charAt(cleanFileName.length - 4).toUpperCase();
      const formattedFileName = cleanFileName.slice(-2);
      return difficultyChar + formattedFileName;
    }
  };

  const getCurrentDisplayText = () => {
    // Determine effectiveId - use currentFile if set, otherwise use selectedVideo/selectedNote/selectedQuestion
    let effectiveId = currentFile;
    
    // Helper to check if content type is question type (MCQ/Coding/Project Coding) - case insensitive
    const contentTypeLower = typeof contentType === 'string' ? contentType.toLowerCase() : '';
    const isQuestionType = contentType === ContentTypeConstants.MCQ.key || contentType === ContentTypeConstants.QUIZ.key || contentType === ContentTypeConstants.CODING.key || contentType === ContentTypeConstants.PROJECT_CODING.key;
    
    // If currentFile is default, use selectedVideo/selectedNote/selectedQuestion
    if (currentFile === CONSTANTS.DEFAULT_QN) {
      if (contentType === ContentTypeConstants.NOTES.key && selectedNote) {
        effectiveId = selectedNote;
      } else if (contentType === ContentTypeConstants.VIDEO.key && selectedVideo) {
        effectiveId = selectedVideo;
      } else if (isQuestionType && selectedQuestion) {
        effectiveId = selectedQuestion;
      }
      // If still default and no session-selected id, show create-new text
      if (effectiveId === CONSTANTS.DEFAULT_QN) {
        if (contentType === ContentTypeConstants.NOTES.key) return "Create New Notes";
        if (contentType === ContentTypeConstants.VIDEO.key) return "Create New Video";
        if (contentTypeLower === ContentTypeConstants.MCQ.key) return "Create New MCQ";
        if (contentTypeLower === ContentTypeConstants.QUIZ.key) return "Create New Quiz";
        if (contentTypeLower === ContentTypeConstants.CODING.key) return "Create New Coding";
        if (contentType === ContentTypeConstants.PROJECT_CODING.key) return "Select Project";
        return CONSTANTS.SELECT;
      }
    }

    // For video/notes/questions, if currentFile matches selectedVideo/selectedNote/selectedQuestion, prefer using those for ID matching
    // This ensures we can use loaded details even if currentFile is already set
    if (contentType === ContentTypeConstants.VIDEO.key && selectedVideo && String(currentFile) === String(selectedVideo)) {
      effectiveId = selectedVideo;
    } else if (contentType === ContentTypeConstants.NOTES.key && selectedNote && String(currentFile) === String(selectedNote)) {
      effectiveId = selectedNote;
    } else if (isQuestionType && selectedQuestion && String(currentFile) === String(selectedQuestion)) {
      effectiveId = selectedQuestion;
    }

    // Always prioritize numbered format (video1, notes1, etc.) over titles
    // This ensures consistent display regardless of where the user navigated from
    // Titles are only used as a fallback if numbered format is not available

    // If loaded details not available, try to find the option in filteredOptions
    const currentOption = filteredOptions.find(option => {
      if (isNotesType || isVideoType) {
        // Convert both to strings for comparison to handle type mismatches
        return String(option.id) === String(effectiveId);
      } else if (isQuestionType) {
        // For MCQ and Coding, compare question ID strings directly
        return String(option) === String(effectiveId);
      } else {
        return option === effectiveId;
      }
    });

    if (currentOption) {
      return getDisplayText(currentOption);
    }
    
    // If not found in filteredOptions, try to find in DisplayFiles, allVideos/allNotes
    // Always prioritize numbered format (video1, notes1, etc.) over titles
    if (contentType === ContentTypeConstants.VIDEO.key) {
      // First, try DisplayFiles (might have the option with numbered format)
      if (DisplayFiles && DisplayFiles.length > 0) {
        const displayOption = DisplayFiles.find(video => String(video.id) === String(effectiveId));
        if (displayOption) {
          // getDisplayText will prioritize video property (video1, video2, etc.) over title
          return getDisplayText(displayOption);
        }
      }
      
      // Then try allVideos (this will show the number via getDisplayText)
      if (allVideos && Object.keys(allVideos).length > 0) {
        const allVideosList = Object.values(allVideos).flat();
        const videoOption = allVideosList.find(video => String(video.id) === String(effectiveId));
        if (videoOption) {
          // getDisplayText will prioritize video property (video1, video2, etc.) over title
          return getDisplayText(videoOption);
        }
      }
      
      // Only use title as last resort if numbered format is not available
      if (videoTitle) {
        return videoTitle;
      }
      if (videoDetails && videoDetails.title) {
        return videoDetails.title;
      }
      
      // Fallback: show video ID
      return `Video ${effectiveId}`;
    } else if (contentType === ContentTypeConstants.NOTES.key) {
      // First, try DisplayFiles (might have the option with numbered format)
      if (DisplayFiles && DisplayFiles.length > 0) {
        const displayOption = DisplayFiles.find(note => String(note.id) === String(effectiveId));
        if (displayOption) {
          // getDisplayText will prioritize notes property (notes1, notes2, etc.) over title
          return getDisplayText(displayOption);
        }
      }
      
      // Then try allNotes (this will show the number via getDisplayText)
      if (allNotes && Object.keys(allNotes).length > 0) {
        const allNotesList = Object.values(allNotes).flat();
        const noteOption = allNotesList.find(note => String(note.id) === String(effectiveId));
        if (noteOption) {
          // getDisplayText will prioritize notes property (notes1, notes2, etc.) over title
          return getDisplayText(noteOption);
        }
      }
      
      return `Notes ${effectiveId}`;
    } else if (isQuestionType) {
      // For MCQ and Coding, if no exact match found, still show the formatted question ID
      if (effectiveId && effectiveId !== CONSTANTS.DEFAULT_QN) {
      return getDisplayText(effectiveId);
      } else if (selectedQuestion) {
        // If effectiveId is default but selectedQuestion exists, use it
        return getDisplayText(selectedQuestion);
      }
      return CONSTANTS.SELECT;
    } else {
      return CONSTANTS.SELECT;
    }
  };

  return (
    <div className="">
      <div className="container-fluid">
        <div className="row p-0">
          <div className="col-6 p-1 ">
            <label
              htmlFor="chooseQuestion"
              className="form-label"
            >
              Choose Level :
            </label>
            <select
              className="form-select custom-input"
              id="quelevel"
              onChange={handleRadioChangeDiff}
              value={selectedDifficulty}
              disabled={isFrozen || currentFile !== CONSTANTS.DEFAULT_QN}
            >
              <option value="default">Select</option>
              <option value="level1">Level 1</option>
              <option value="level2">Level 2</option>
              <option value="level3">Level 3</option>
            </select>
          </div>
          <div className="col-6 p-1">
            <label
              htmlFor="chooseQuestion"
              className="form-label"
            >
              {contentType === ContentTypeConstants.NOTES.key ? "Choose Notes :" : contentType === ContentTypeConstants.VIDEO.key ? "Choose Video :" : "Choose Question :"}
            </label>
            <div className="custom-dropdown" ref={dropdownRef} style={{ position: 'relative' }}>
              <div 
                className="form-select custom-input"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!isFrozen) {
                    setIsOpen(!isOpen);
                  } else {
                  }
                }}
                style={{ cursor: isFrozen ? 'not-allowed' : 'pointer', opacity: isFrozen ? 0.6 : 1, pointerEvents: isFrozen ? 'none' : 'auto' }}
                title={isFrozen ? 'Filters are frozen - cannot change' : ''}
              >
                {getCurrentDisplayText()}
              </div>
              
              {isOpen && !isFrozen && (
                <div 
                  className="dropdown-menu"
                  style={{ 
                    display: 'block', 
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 1050,
                    backgroundColor: '#fff',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    maxHeight: '200px',
                    overflowY: 'auto'
                  }}
                  onClick={(e) => {
                    // Prevent clicks inside dropdown from closing it
                    e.stopPropagation();
                  }}
                >
                  {contentType !== ContentTypeConstants.PROJECT_CODING.key && (
                    <div 
                      className={`dropdown-item ${currentFile === CONSTANTS.DEFAULT_QN ? 'selected' : ''}`}
                      onClick={() => handleOptionClick(CONSTANTS.DEFAULT_QN)}
                    >
                      {contentType === ContentTypeConstants.NOTES.key ? "Create New Notes" : 
                       contentType === ContentTypeConstants.VIDEO.key ? "Create New Video" : 
                       contentType === ContentTypeConstants.MCQ.key ? "Create New MCQ" :
                       contentType === ContentTypeConstants.QUIZ.key ? "Create New Quiz" :
                       contentType === ContentTypeConstants.CODING.key ? "Create New Coding" : CONSTANTS.SELECT}
                    </div>
                  )}
                  
                  {filteredOptions.length > 0 ? (
                    filteredOptions.map((option) => {
                      const value = (isNotesType || isVideoType) ? option.id : option;
                      // Check if this option is the currently selected one
                      const isQuestionType = contentType === ContentTypeConstants.MCQ.key || contentType === ContentTypeConstants.QUIZ.key || contentType === ContentTypeConstants.CODING.key ||
                        contentType === ContentTypeConstants.PROJECT_CODING.key;
                      const isSelected = String(currentFile) === String(value) ||
                        (isVideoType && String(selectedVideo) === String(value)) ||
                        (isNotesType && String(selectedNote) === String(value)) ||
                        (isQuestionType && String(selectedQuestion) === String(value));
                      
                      return (
                        <div 
                          key={value}
                          className={`dropdown-item ${isSelected ? 'selected' : ''}`}
                          onClick={() => handleOptionClick(value)}
                        >
                          {getDisplayText(option)}
                        </div>
                      );
                    })
                  ) : (
                    <div className="dropdown-item disabled">
                      {contentType === ContentTypeConstants.NOTES.key ? "No Notes available" : 
                       contentType === ContentTypeConstants.VIDEO.key ? "No Videos available" : 
                       "No Questions available"}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionSelector;
